import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  safetyStockService,
  type SafetyStockBacktestBody,
  type SafetyStockManualBody,
  type SafetyStockSkuActionBody,
} from '@/services/safetyStockService';

const STALE_MS = 60 * 1000;
const GC_MS = 10 * 60 * 1000;

export const safetyStockPoliciesQueryKey = ['safety-stock-policies'] as const;
export const safetyStockPolicyFiltersQueryKey = ['safety-stock-policy-filters'] as const;

export function useSafetyStockPolicyFilters() {
  return useQuery({
    queryKey: safetyStockPolicyFiltersQueryKey,
    queryFn: ({ signal }) => safetyStockService.getPolicyFilters({ signal }),
    staleTime: 5 * 60 * 1000,
    gcTime: GC_MS,
  });
}

export function useSafetyStockPolicies(
  params: Record<string, string | number | boolean | string[] | number[]>,
  enabled = true,
) {
  return useQuery({
    queryKey: [...safetyStockPoliciesQueryKey, params],
    enabled,
    queryFn: ({ signal }) => safetyStockService.listPolicies(params, { signal }),
    staleTime: STALE_MS,
    gcTime: GC_MS,
    placeholderData: (prev) => prev,
  });
}

export function useSafetyStockPolicyMutations() {
  const queryClient = useQueryClient();
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: safetyStockPoliciesQueryKey });

  const preview = useMutation({
    mutationFn: (body: SafetyStockSkuActionBody) => safetyStockService.previewPolicy(body),
  });
  const apply = useMutation({
    mutationFn: (body: SafetyStockSkuActionBody) => safetyStockService.applyPolicy(body),
    onSuccess: invalidate,
  });
  const manual = useMutation({
    mutationFn: (body: SafetyStockManualBody) => safetyStockService.manualPolicy(body),
    onSuccess: invalidate,
  });
  const backtest = useMutation({
    mutationFn: (body: SafetyStockBacktestBody) => safetyStockService.backtestPolicy(body),
    onSuccess: (_data, vars) => {
      if (vars.persist) invalidate();
    },
  });
  const validate = useMutation({
    mutationFn: (body: SafetyStockSkuActionBody) => safetyStockService.validatePolicy(body),
  });

  return { preview, apply, manual, backtest, validate, invalidate };
}
